/**
 * Admin 主题系统（Phase02）—— light / dark / system 三态
 *
 * 零依赖（Vanilla JS + CSS Variables），不引入主题库。
 * - 存储：localStorage['admin-theme'] = 'light' | 'dark' | 'system'（默认 system）
 * - 挂载：document.body.classList.toggle('dark')——全局唯一挂载点（挂 body 非 html，
 *   原因见 applyToDocument 注释：postcss-rtl 的 [dir] 前缀选择器与 html 级 .dark 互斥）
 * - system：matchMedia('(prefers-color-scheme: dark)') 监听，系统切换实时同步；
 *   手动 light/dark 不跟系统。浏览器不支持 matchMedia 时 system 回退 light。
 * - 作用域：仅 PC Admin（/admin 路径）。离开 admin 时由 AdminLayout 调 disposeTheme()
 *   摘除 .dark，避免污染未 token 化的 C 端/移动端页面。
 */

const STORAGE_KEY = 'admin-theme'
const MODES = ['system', 'light', 'dark']

let mediaQuery = null
let mediaListener = null

function readStoredMode () {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return MODES.includes(v) ? v : 'system'
  } catch (e) {
    return 'system' // localStorage 不可用（隐私模式等）→ 默认 system
  }
}

function systemPrefersDark () {
  return !!(typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches)
}

function applyToDocument (dark) {
  // 注意：挂 body 而非 html。<html> 自带 dir 属性，postcss-rtl 生成的
  // `[dir] .dark …` 选择器要求 .dark 是 [dir] 元素的后代，挂 html 时这类
  // 规则（dark 下按钮 background/border 覆盖）永不匹配。挂 body 则
  // html[dir] → body.dark 链成立，且变量级联范围不变（body 子树=全部可视 DOM）。
  document.body.classList.toggle('dark', dark)
}

/** 解析某个模式当前是否应为夜间（含 system 回退 light） */
export function isDarkActive (mode) {
  if (mode === 'dark') return true
  if (mode === 'system') return systemPrefersDark() // 不支持 matchMedia → false → light
  return false
}

/** 启动/刷新主题：读取存储 → 挂 .dark → system 模式挂监听。Admin 进入时调用（main.js 早期 + AdminLayout created） */
export function initTheme () {
  const mode = readStoredMode()
  applyToDocument(isDarkActive(mode))
  bindMediaListener(mode)
  return mode
}

/** 切换模式：写 localStorage → 立即生效 → 重绑监听 */
export function setTheme (mode) {
  if (!MODES.includes(mode)) mode = 'system'
  try { localStorage.setItem(STORAGE_KEY, mode) } catch (e) { /* 存不进也照样生效，仅刷新后回默认 */ }
  applyToDocument(isDarkActive(mode))
  bindMediaListener(mode)
}

/** 当前存储的模式（不重算 dark 状态） */
export function getTheme () {
  return readStoredMode()
}

/** system 模式专用监听：系统明暗切换实时同步；手动模式不挂 */
function bindMediaListener (mode) {
  if (mediaListener) {
    if (mediaQuery && mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', mediaListener)
    mediaListener = null
    mediaQuery = null
  }
  if (mode !== 'system' || typeof window === 'undefined' || !window.matchMedia) return
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaListener = e => applyToDocument(e.matches)
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', mediaListener)
  } else if (mediaQuery.addListener) {
    mediaQuery.addListener(mediaListener) // 老 Safari
  }
}

/** 离开 Admin 时调用：摘 .dark + 移除监听 */
export function disposeTheme () {
  applyToDocument(false)
  bindMediaListener('light') // 任何非 system 模式都会走解绑分支
}
